import { BigNumber, ethers } from 'ethers';
import { useCallback, useMemo, useState } from 'react';
import { useTransactionAdder } from '../state/transactions/hooks';
import useAllowance from './useAllowance';
import { useNetworkName } from './useNetwork';
import { useOnchainInteractionsPoolToken } from './useOnchain';
import { ButtonState } from '../components/InteractionButton';
import { useOffchainLendingPool, useOffchainPoolToken } from './useOffchain';
import useGenericCall from './useGenericCall';
import { Amms, ApprovalType, Networks, PoolTokenType } from 'impermax-sdk';

const ZERO = ethers.constants.Zero;
const APPROVE_AMOUNT = ethers.constants.MaxUint256;

export interface PermitData {
  permitData: string;
  amount: BigNumber;
  deadline: BigNumber;
}

// returns a variable indicating the state of the approval and a function which approves if necessary or early returns
export default function useApprove(approvalType: ApprovalType, amount: BigNumber, invalidInput: boolean, poolTokenTypeArg?: PoolTokenType, deadline?: BigNumber): [ButtonState, () => Promise<void>, PermitData] {
  const networkName = useNetworkName();
  const onchainInteractionsPoolToken = useOnchainInteractionsPoolToken(poolTokenTypeArg);
  const addTransaction = useTransactionAdder();
  const [pending, setPending] = useState<boolean>(false);
  const [permitData, setPermitData] = useState<PermitData>(null);
  const currentAllowance = useAllowance(approvalType, pending, poolTokenTypeArg);
  const amm = useGenericCall(useOffchainLendingPool(), (s) => s.getAmm());

  const symbol = useGenericCall(useOffchainPoolToken(), poolToken => poolToken.getSymbol());
  const action = approvalType === ApprovalType.BORROW ? 'borrow' : approvalType === ApprovalType.POOL_TOKEN ? 'withdrawal' : 'transfer'
  const summary = `Approve ${symbol} ${action}`;

  const approvalState: ButtonState = useMemo(() => {
    if (invalidInput) return ButtonState.Disabled;
    if (!currentAllowance) return ButtonState.Disabled;
    if (!amount || amount.eq(ZERO)) return ButtonState.Disabled;
    return currentAllowance.lt(amount) && (permitData === null || !permitData.amount.eq(amount))
      ? pending
        ? ButtonState.Pending
        : ButtonState.Ready
      : ButtonState.Done;
  }, [invalidInput, currentAllowance, pending, amount]);

  const approve = useCallback(async (): Promise<void> => {
    if (approvalState !== ButtonState.Ready) return;
    setPending(true);

    const traditionalApprove = async () => {
      setPermitData(null);
      try {
        const response = await onchainInteractionsPoolToken.approve(approvalType, APPROVE_AMOUNT, (hash: string) => {
          addTransaction({ hash }, { summary });
        });
      } catch (err) {
        console.error(err);
      }
      setPending(false);
    };

    // if (true) {//networkName === Networks.Mainnet || networkName === Networks.Arbitrum) {
    if (
      networkName === Networks.Mantle ||
      (networkName == Networks.Base && amm == Amms.aerodrome) ||
      (networkName == Networks.Optimism && amm == Amms.velodrome) ||
      (networkName == Networks.Sonic && amm == Amms.shadow) ||
      (networkName == Networks.Linea && amm == Amms.etherex)
    ) {
      traditionalApprove();
    } else {
      onchainInteractionsPoolToken.getPermitData(approvalType, amount, deadline, async (permitData: PermitData) => {
        if (permitData) {
          setPermitData(permitData);
          setPending(false);
          return;
        }
        // Fallback to
        traditionalApprove();
      });
    }
  }, [approvalState, onchainInteractionsPoolToken, addTransaction, amount, deadline]);

  return [approvalState, approve, permitData];
}
